import React, {
  useMemo, useRef, useState, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Confirm,
  Dropdown,
  Icon,
  Input,
  Popup,
} from 'semantic-ui-react';
import './ProductSelector.css';
import { getProductType } from '../constants/productFamily';

// Helper function to get icon for product type
const getProductIcon = productType => {
  if (productType === 'all-products') {
    return 'database';
  }
  if (productType === 'seller-assurance') {
    return 'check circle';
  }
  return 'shield';
};

// Custom render function for dropdown options
const renderProductOption = item => {
  const type = getProductType(item.text);
  const iconName = getProductIcon(type);
  let typeClass = 'dealshield';
  if (type === 'seller-assurance') typeClass = 'seller-assurance';
  if (type === 'all-products') typeClass = 'all-products';

  return (
    <div className='eps-option-content'>
      <div className={`eps-icon-box ${typeClass}`}>
        <Icon name={iconName} />
      </div>
      <span className='eps-option-text'>{item.text}</span>
    </div>
  );
};

const ProductSelector = ({
  products,
  selectedProduct,
  onProductChange,
  hasUnsavedChanges,
  tooltipContent,
  placeholderText,
  themeClass,
}) => {
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);
  const menuRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // When the user picks a product while edits are pending, hold the target here
  // until they confirm the switch via the dialog.
  const [pendingProductId, setPendingProductId] = useState(null);

  const handleOpen = () => {
    setIsOpen(true);
    setSearchQuery('');
    setTimeout(() => {
      if (searchRef.current) {
        searchRef.current.focus();
      }
    }, 0);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSearchChange = (e, { value }) => {
    setSearchQuery(value);
  };

  const handleItemClick = productId => {
    handleClose();
    if (hasUnsavedChanges) {
      setPendingProductId(productId);
    } else {
      onProductChange(productId);
    }
  };

  const handleConfirmSwitch = () => {
    if (pendingProductId) {
      onProductChange(pendingProductId);
    }
    setPendingProductId(null);
  };

  const handleCancelSwitch = () => {
    setPendingProductId(null);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const onPointerDown = e => {
      const { target } = e;
      if (
        (wrapperRef.current && wrapperRef.current.contains(target))
        || (menuRef.current && menuRef.current.contains(target))
      ) {
        return;
      }
      setIsOpen(false);
    };

    const onKeyDown = e => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const selectedProductData = useMemo(
    () => products.find(p => p.categoryCode === selectedProduct),
    [products, selectedProduct]
  );

  const productType = useMemo(
    () => getProductType(selectedProductData?.displayName),
    [selectedProductData]
  );

  const filteredProducts = useMemo(() => {
    if (!searchQuery) return products;
    const lower = searchQuery.toLowerCase();
    return products.filter(p => p.displayName.toLowerCase().includes(lower));
  }, [products, searchQuery]);

  return (
    <div
      id='ProductSelector'
      data-testid='product-selector'
      ref={wrapperRef}
      className={isOpen ? 'dropdown-open' : ''}
    >
      <Confirm
        open={pendingProductId !== null}
        className={`eps-confirm-modal theme ${themeClass}`}
        header='Unsaved Changes'
        content='You have unsaved changes. Switching products will discard them. Do you want to continue?'
        cancelButton='Stay'
        confirmButton='Leave'
        onCancel={handleCancelSwitch}
        onConfirm={handleConfirmSwitch}
      />
      <Popup
        content={tooltipContent}
        position='right center'
        inverted
        disabled={isOpen}
        trigger={(
          <Dropdown
            open={isOpen}
            onOpen={handleOpen}
            onClose={handleClose}
            closeOnBlur={false}
            closeOnDocumentClick={false}
            trigger={(
              <Button
                className={`eps-dropdown ${themeClass}`}
                aria-haspopup='listbox'
                aria-expanded={isOpen}
                id='eps-trigger'
                data-testid='eps-trigger-button'
              >
                <Icon name={getProductIcon(productType)} />
                <span className='eps-text'>
                  {selectedProductData?.displayName || placeholderText}
                </span>
                <Icon name='chevron down' className='eps-arrow' />
              </Button>
            )}
            icon={null}
          >
            <Dropdown.Menu
              ref={menuRef}
              className='eps-custom-menu'
              id='eps-menu'
              role='listbox'
              aria-labelledby='eps-trigger'
            >
              <div className='eps-search-container'>
                <Input
                  ref={searchRef}
                  icon='search'
                  iconPosition='left'
                  placeholder='Search products...'
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onClick={e => e.stopPropagation()}
                  className='eps-search-input'
                  data-testid='eps-search-input'
                  fluid
                />
              </div>
              <Dropdown.Menu scrolling>
                {filteredProducts.map(product => {
                  const type = getProductType(product.displayName);
                  return (
                    <Dropdown.Item
                      key={product.categoryCode}
                      onClick={() => handleItemClick(product.categoryCode)}
                      className={`eps-option eps-option-${type}`}
                      style={{ borderTop: 'none' }}
                      active={product.categoryCode === selectedProduct}
                      data-testid={`eps-product-item-${product.categoryCode}`}
                    >
                      {renderProductOption({ text: product.displayName })}
                    </Dropdown.Item>
                  );
                })}
                {filteredProducts.length === 0 && (
                  <Dropdown.Item disabled data-testid='eps-no-products-found'>
                    <div
                      className='eps-option-content'
                      style={{ padding: '10px 16px', color: '#999' }}
                    >
                      No products found
                    </div>
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown.Menu>
          </Dropdown>
        )}
      />
    </div>
  );
};

ProductSelector.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      categoryCode: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedProduct: PropTypes.string.isRequired,
  onProductChange: PropTypes.func.isRequired,
  hasUnsavedChanges: PropTypes.bool,
  tooltipContent: PropTypes.string,
  placeholderText: PropTypes.string,
  themeClass: PropTypes.string,
};

ProductSelector.defaultProps = {
  hasUnsavedChanges: false,
  tooltipContent: 'Select product context.',
  placeholderText: 'SELECT PRODUCT CONTEXT',
  themeClass: 'theme-blue',
};

export default ProductSelector;
